import React, { useRef, useState, useEffect, memo } from 'react';
import ReactEChartsCore from '../EChart';
import * as echarts from 'echarts/core';
import { Card } from 'react-bootstrap';
import {
  TooltipComponent,
  TitleComponent,
  GridComponent
} from 'echarts/components';
import { BarChart } from 'echarts/charts';
import { CanvasRenderer } from 'echarts/renderers';
import axios from 'axios';
import { FallingLines } from 'react-loader-spinner';
import { useAppContext } from '../../../providers/AppProvider';
import { isLiveTimeRange } from '../utils/chartUtils';
import { INFLUX_API } from '../../../config/api';

echarts.use([
  TooltipComponent,
  TitleComponent,
  GridComponent,
  BarChart,
  CanvasRenderer
]);

const getDefaultOptions = (theme, getThemeColor, diameterData) => ({
  color: [getThemeColor('primary')],
  tooltip: {
    trigger: 'axis',
    padding: 10,
    backgroundColor: getThemeColor('gray-100'),
    borderColor: getThemeColor('gray-300'),
    textStyle: { color: getThemeColor('dark') },
    borderWidth: 1,
    transitionDuration: 0,
    axisPointer: {
      type: 'shadow'
    },
    formatter: params => {
      const { name, value } = params[0] || {};
      return `Diameter: ${name}<br/>Total Produced: ${value} meters`;
    }
  },
  xAxis: {
    type: 'category',
    data: diameterData.map(item => item[0]), // DiameterSetValue on x-axis
    axisLabel: {
      color: getThemeColor('gray-800'),
      fontFamily: 'Nunito Sans',
      fontWeight: 600,
      fontSize: 12.8
    },
    axisLine: {
      lineStyle: {
        color: getThemeColor('gray-300')
      }
    },
    axisTick: false
  },
  yAxis: {
    type: 'value',
    axisLine: { lineStyle: { color: getThemeColor('gray-300') } },
    splitLine: { show: true, lineStyle: { color: getThemeColor('gray-300') } },
    axisLabel: { color: getThemeColor('gray-900'), formatter: '{value} meters' }
  },
  series: [
    {
      name: 'Total Produced',
      type: 'bar',
      data: diameterData.map(item => item[1]), // Total Produced as bar height
      barWidth: '6px',
      itemStyle: { borderRadius: [2, 2, 0, 0], color: getThemeColor('primary') }
    }
  ],
  grid: {
    right: 40,
    left: 20,
    bottom: '20px',
    top: '10%',
    containLabel: true
  },
  animation: false
});

/**
 * @param field the diameter SETPOINT tag
 * @param gate  optional ON/OFF tag saying whether this line's gauge was
 *              measuring. The L120 is a tandem: two extruders with a laser each,
 *              running independently, and an idle line's setpoint keeps its last
 *              value — so without a gate every metre of a filler-only run was
 *              credited to a jacketing diameter that never touched the cable.
 *              Omit it on a single-extruder line, where every metre counts.
 */
const DiameterSetTotalProduced = ({
  bucket,
  machineName,
  startTime,
  endTime,
  title,
  field,
  gate
}) => {
  const chartRef = useRef(null);
  const [diameterData, setDiameterData] = useState([]);
  const [totals, setTotals] = useState(null);
  const [loading, setLoading] = useState(true);

  const {
    config: { theme },
    getThemeColor
  } = useAppContext();

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${INFLUX_API}/api/influx/${machineName}/diameter-production`,
        { params: { start: startTime, end: endTime, bucket, field, gate } }
      );
      // The endpoint used to return a bare array and now returns
      // `{ bars, totals }`. Both are accepted so a cached bundle, or a browser
      // talking to a service that has not been restarted yet, still draws.
      const payload = response.data || [];
      const bars = Array.isArray(payload) ? payload : payload.bars || [];
      setTotals(Array.isArray(payload) ? null : payload.totals || null);

      setDiameterData(
        bars
          .filter(({ totalProduced }) => totalProduced >= 10)
          .map(({ diameterValue, totalProduced }) => [
            Number(diameterValue).toFixed(2),
            Number(totalProduced).toFixed(2)
          ])
      );
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    const intervalId = isLiveTimeRange(endTime) ? setInterval(fetchData, 60000) : null;
    return () => clearInterval(intervalId);
    // `field` and `gate` are part of the question being asked — leaving them out
    // meant the two L120 charts could keep each other's data on a re-render.
  }, [bucket, machineName, startTime, endTime, field, gate]);

  if (loading) {
    return (
      <Card className="h-0">
        <Card.Body style={{ height: '316px', width: '100%' }}>
          <div style={styles.spinnerContainer}>
            <FallingLines
              color={theme === 'dark' ? 'white' : '#3874ff'}
              width="100"
              visible={true}
              ariaLabel="falling-circles-loading"
            />
          </div>
        </Card.Body>
      </Card>
    );
  }

  return (
    <Card className="h-100">
      <Card.Body>
        <div style={styles.headerContainer}>
          <div style={styles.title}>{title}</div>
          {/* What the bars leave out. A chart that silently drops half a line's
              output is how this went unnoticed for so long: the jacketing chart
              was showing 274 688 m produced with its own laser switched off and
              nothing said so. */}
          {totals && (
            <div className="text-end text-700 fs-10" style={{ whiteSpace: 'nowrap' }}>
              <div>{Math.round(totals.counted).toLocaleString()} m counted</div>
              {gate && totals.gatedOut > 0 && (
                <div title={`Produced while the gauge was off — made by the other extruder`}>
                  {Math.round(totals.gatedOut).toLocaleString()} m gauge off
                </div>
              )}
            </div>
          )}
        </div>
        <ReactEChartsCore
          ref={chartRef}
          echarts={echarts}
          option={getDefaultOptions(theme, getThemeColor, diameterData)}
          style={{ height: '316px', width: '100%' }}
        />
      </Card.Body>
    </Card>
  );
};

const styles = {
  spinnerContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100%',
    width: '100%'
  },
  headerContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '10px'
  },
  title: {
    fontSize: '16px',
    fontWeight: 'bold',
    // minWidth lets a long title wrap instead of shoving the resolution
    // picker out of the card: a flex item will not shrink below its
    // content width until it is told it may.
    flex: '1 1 auto',
    minWidth: 0,
    lineHeight: 1.25,
    paddingRight: '8px'
  }
};

export default memo(DiameterSetTotalProduced);
